import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import {
  FormProvider,
  type SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box } from '~/components/elements/Box'
import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { VStack } from '~/components/elements/VStack'
import { useLogEvent } from '~/hooks/useLogEvent'
import {
  ensureExists,
  findByPath,
  mapItemsToObj,
  unassignedSymbol,
} from '~/utils'

import { dialogInfoAtom } from '../../atoms'
import { useCloseDialogWithRefresh } from '../../hooks/useCloseDialogWithRefresh'
import { useUpdateBrandsMutation } from '../../hooks/useUpdateBrandsMutation'

import { BrandInput } from './BrandInput'
import { type InputValues, resolver } from './utils'

export const BrandSettingsDialog = () => {
  const { t } = useTranslation()
  const dialogInfo = useAtomValue(dialogInfoAtom)
  const surveyGroup = ensureExists(
    dialogInfo?.surveyGroup,
    t('assertion_failed'),
  )
  const defaultBrands = React.useMemo(
    () =>
      surveyGroup.brands.map((brand) => ({
        id: brand.id,
        name: brand.name,
        attachedBrandMap: mapItemsToObj(surveyGroup.surveys, (survey) => {
          const attachedBrand = findByPath(
            brand.attachedBrands,
            'surveyId',
            survey.id,
          )

          return attachedBrand?.brandId ?? unassignedSymbol
        }),
      })) satisfies InputValues['brands'],
    [surveyGroup.brands, surveyGroup.surveys],
  )

  const methods = useForm<InputValues>({
    defaultValues: { brands: defaultBrands },
    resolver,
  })
  const {
    control,
    formState: { isDirty },
    handleSubmit,
  } = methods
  const { append, fields, remove } = useFieldArray({
    control,
    keyName: 'fieldId',
    name: 'brands',
  })

  const handleAddButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    append({
      name: '',
      attachedBrandMap: mapItemsToObj(
        surveyGroup.surveys,
        () => unassignedSymbol,
      ),
    })
  }, [append, surveyGroup.surveys])

  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const updateBrandsMutation = useUpdateBrandsMutation({
    onSuccess: closeDialogWithRefresh,
  })

  const logEvent = useLogEvent()
  const resetDialogInfo = useResetAtom(dialogInfoAtom)
  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    ({ brands }) => {
      logEvent('Define brand setting')

      if (!isDirty) {
        resetDialogInfo()

        return
      }

      updateBrandsMutation.mutate({
        brands,
        defaultBrands,
        surveyGroupId: surveyGroup.id,
      })
    },
    [
      defaultBrands,
      isDirty,
      logEvent,
      resetDialogInfo,
      surveyGroup.id,
      updateBrandsMutation,
    ],
  )

  return (
    <FormProvider {...methods}>
      <ExecuteCancelDialog
        executing={updateBrandsMutation.isPending}
        mode="settings"
        title={t('surveygroup_title_brandsettings', {
          surveyGroup,
        })}
        onCancel={resetDialogInfo}
        onExecute={handleSubmit(handleValid)}
      >
        <VStack spacing={0}>
          <Box padding={2}>
            <StyledButton icon="add" onClick={handleAddButtonClick}>
              {t('add_brand_2')}
            </StyledButton>
          </Box>

          {fields.map((field, index) => (
            <BrandInput key={field.fieldId} index={index} onDelete={remove} />
          ))}
        </VStack>
      </ExecuteCancelDialog>
    </FormProvider>
  )
}
